import ApiService from './api.service';

export type DashboardDateRange = {
  date_range?: string;
  custom_date_range_start?: string;
  custom_date_range_end?: string;
  user_timezone?: string;
  previous_period?: boolean;
};

export type DashboardFilterOption = {
  id?: string;
  filter_type: string;
  description?: string;
  column_references?: any[];
  comparator?: string;
  query_list_node_id?: string;
  query_list_field_id?: string;
  static_list?: string[];
  allow_multiple?: boolean;
  default_value?: string | string[];
  dimension_id?: string;
  input_label?: string;
};

/**
 * Color overrides. Set on a dashboard, on one component, or on the tenant's
 * whitelabel config — each tier writes the same CSS variables onto a narrower
 * element, so an unset field inherits rather than resetting to the default.
 * See lib/dashboardTheme.ts for the field list and the variables they feed.
 */
export type DashboardTheme = {
  primary_color?: string | null;
  page_bg_color?: string | null;
  card_bg_color?: string | null;
  text_color?: string | null;
  border_color?: string | null;
  chart_1_color?: string | null;
  chart_2_color?: string | null;
  chart_3_color?: string | null;
  chart_4_color?: string | null;
  chart_5_color?: string | null;
};

export type DashboardComponent = {
  id: string;
  title: string;
  description?: string;
  x: number;
  y: number;
  width: number;
  height: number;
  object_type?: string;
  object_id?: string;
  markdown_content?: string;
  filter_config?: DashboardFilterOption;
  column_whitelist?: string[];
  ignore_date_range?: boolean;
  hide_header?: boolean;
  hide_toolbar?: boolean;
  disable_sort?: boolean;
  // object_type 'INLINE_ANALYSIS': the analysis definition lives on the component
  // rather than in its own document, so a generated dashboard is self-contained.
  analysis_definition?: Record<string, any>;
  // Colors for this panel only, overriding the dashboard's and the tenant's.
  theme?: DashboardTheme | null;
};

export type DashboardPresentation = {
  // Render without the Pliable app shell (sidebar, global nav). Presentation
  // only — auth is unchanged, the viewer still has to log in.
  chromeless?: boolean;
  // Also drop the in-page header (breadcrumbs, title, description).
  hide_page_header?: boolean;
  // Open with the app sidebar collapsed to icons. Unlike chromeless, the
  // sidebar is still there and the viewer can expand it.
  compact_sidebar?: boolean;
  background_color?: string | null;
  max_width?: number | null;
};

export type DashboardFilterBarOption = DashboardFilterOption & { id?: string };

export type Dashboard = {
  id: string;
  name: string;
  description: string;
  components: DashboardComponent[];
  version_idx?: number;
  date_range_config?: DashboardDateRange;
  last_updated?: string;
  created_by?: string;
  presentation?: DashboardPresentation;
  // Filters belonging to the dashboard rather than a panel; rendered in the
  // filter bar above the grid.
  filters?: DashboardFilterBarOption[];
  // Colors for the whole dashboard, overriding the tenant's whitelabeling.
  theme?: DashboardTheme | null;
};

export type SingleRecordResponse<T> = {
  record: T;
};

export type ListRecordsResponse<T> = {
  records: T[];
  total_pages?: number;
  total_records?: number;
};

export class DashboardService {
  async find(options?: {
    page?: number;
    pageSize?: number;
    filters?: Record<string, any>;
  }): Promise<ListRecordsResponse<Dashboard>> {
    const {
      page = 1,
      pageSize = 50,
      filters = {},
    } = options || {};

    const queryParams: Record<string, any> = {
      page,
      limit: pageSize,
      ...filters,
    };

    const result = await ApiService.getInstance().request(
      'GET',
      '/dashboards',
      queryParams
    ) as ListRecordsResponse<Dashboard>;

    return result;
  }

  async findById(id: string): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'GET',
      `/dashboards/${id}`
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async create(dashboard: Omit<Dashboard, 'id' | 'last_updated'>): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'POST',
      '/dashboards',
      dashboard
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async update(id: string, updates: Partial<Dashboard>): Promise<Dashboard> {
    const result = await ApiService.getInstance().request(
      'PATCH',
      `/dashboards/${id}`,
      updates
    ) as SingleRecordResponse<Dashboard>;
    return result.record;
  }

  async delete(id: string): Promise<void> {
    await ApiService.getInstance().request(
      'DELETE',
      `/dashboards/${id}`
    );
  }
}

export const dashboardService = new DashboardService();

